import { useCallback } from 'react'

import { useMutation } from '@apollo/client/react'

import { getOmniMirrorFlush } from '~publish/components/Composer/store/bridge/omniMirrorFlush'
import {
  projectFluxOmniContent,
  projectFluxThreadAssets,
} from '~publish/components/Composer/store/bridge/projectFluxOmniContent'
import { useSaveErrorActions } from '~publish/components/Composer/store/saveErrorsSlice/hooks'
import { getComposerStore } from '~publish/components/Composer/store/composerStore'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import ComposerActionCreators from '~publish/legacy/composer/composer/action-creators/ComposerActionCreators'
import { DraftMethods } from '~publish/legacy/composer/composer/entities/Draft'
import ComposerStore from '~publish/legacy/composer/composer/stores/ComposerStore'
import AppHooks from '~publish/legacy/composer/composer/utils/LifecycleHooks'

import type { ScheduleOption } from '../graphqlEnums'
import { DRAFT_LIST_REFETCH_QUERIES } from './draftListRefetchQueries'
import { getChannelLessSourceDraft } from '~publish/components/Composer/store/bridge/getChannelLessSourceDraft'
import { mapToDraftContentInput } from './mapToDraftContentInput'
import type { SaveCallbacks } from './types'
import {
  createQueueingType,
  EMPTY_DRAFT_ERROR,
  extractGraphQLMessages,
  GENERIC_SAVE_ERROR,
  saveResult,
} from './useGraphCallbacks'

export const CreateContentItemDraftDocument = graphql(`
  mutation CreateContentItemDraft($input: CreateContentItemDraftInput!) {
    createContentItemDraft(input: $input) {
      __typename
      ... on CreateContentItemDraftSuccess {
        contentItem {
          id
        }
      }
      ... on CreateContentItemDraftFailure {
        message
        errors {
          message
        }
      }
    }
  }
`)

const UpdateContentItemDraftDocument = graphql(`
  mutation UpdateContentItemDraft($input: UpdateContentItemDraftInput!) {
    updateContentItemDraft(input: $input) {
      __typename
      ... on UpdateContentItemDraftSuccess {
        contentItem {
          id
        }
      }
      ... on UpdateContentItemDraftFailure {
        message
        errors {
          ... on MutationError {
            message
          }
        }
      }
    }
  }
`)

/**
 * Save callback that persists the channel-less omni draft, fed from
 * postSlice.omni. Wired in by useSaveCallbacks when there is no channel
 * selected and the channel-agnostic drafts feature is enabled.
 *
 * Routes on `omni.draftId`: a seeded id (opened via Edit) updates that content
 * item in place through updateContentItemDraft; otherwise a new draft is
 * created via createContentItemDraft. Both refetch the draft-list surfaces
 * (see DRAFT_LIST_REFETCH_QUERIES) rather than writing the new body to the
 * cache — the mutation can't embed the gql.tada card fragment.
 *
 * `scheduledAt` (unix seconds from the schedule selector) becomes the content
 * item's `targetDate` — a planning aid on the draft, not an actual schedule —
 * on both create and update. With no scheduled time it is omitted, which keeps
 * any existing targetDate on an edit.
 */
export const useContentItemDraftSave = (
  option: ScheduleOption = 'Queue',
  scheduledAt: number | null = null,
): SaveCallbacks['saveAsDraft'] => {
  const organizationId = useOrganizationId()
  const [createContentItemDraft] = useMutation(CreateContentItemDraftDocument, {
    refetchQueries: DRAFT_LIST_REFETCH_QUERIES,
  })
  const [updateContentItemDraft] = useMutation(UpdateContentItemDraftDocument, {
    refetchQueries: DRAFT_LIST_REFETCH_QUERIES,
  })
  const { setGeneralSaveErrors, clearGeneralSaveErrors } = useSaveErrorActions()

  return useCallback(
    async (options) => {
      clearGeneralSaveErrors()

      // The Flux→Zustand mirror debounces its asset pass, so a media object
      // attached just before Save can still be pending. Settle it before
      // reading `omni` so the draft maps every attachment, not a stale set.
      getOmniMirrorFlush()?.()

      const { omni, selectedTagIds } = getComposerStore().getState()
      // Only relevant on create: an existing draft's title is edited directly
      // from the Header via useUpdateContentItemTitle, since
      // UpdateContentItemDraftInput has no title field.
      const title = omni.title?.trim() || undefined

      // Content is normally on the omni draft. In preview mode the user composes
      // on the selected channel's draft, so source it from there instead.
      const sourceDraft = getChannelLessSourceDraft()
      const channelSourceDraft =
        sourceDraft && sourceDraft.id !== 'omni' ? sourceDraft : undefined

      // The open thread editor's text is not written into thread[] until the
      // active editor switches, so flush it (switch to the current index)
      // before joining, or the post being edited is dropped.
      if (channelSourceDraft?.hasThread()) {
        ComposerActionCreators.switchActiveThreadEditor(
          channelSourceDraft.id,
          ComposerStore.getActiveThreadId(),
        )
      }

      // ContentItem draft supports a single text field, so join threads together
      // to preserve their content when saving channel-less. Media is collected
      // across every thread post, matching the joined text.
      const content = channelSourceDraft
        ? {
            ...omni,
            ...projectFluxOmniContent(channelSourceDraft),
            assets: projectFluxThreadAssets(channelSourceDraft),
          }
        : omni
      const text = channelSourceDraft
        ? (DraftMethods.getJoinedThreadsText(channelSourceDraft) ??
          content.text)
        : omni.text

      const draft = { ...mapToDraftContentInput(content), text }

      if (text.trim() === '' && (draft.assets?.length ?? 0) === 0) {
        setGeneralSaveErrors([EMPTY_DRAFT_ERROR])
        return saveResult(false)
      }

      if (!organizationId) {
        setGeneralSaveErrors([GENERIC_SAVE_ERROR])
        return saveResult(false)
      }

      const draftId = omni.draftId
      // Unix seconds → ISO DateTime, matching the dueAt conversion in mapShareMode.
      const targetDate =
        scheduledAt != null
          ? new Date(scheduledAt * 1000).toISOString()
          : undefined

      const reportSaved = (): ReturnType<typeof saveResult> => {
        AppHooks.handleSavedDrafts({
          fromIdea: false,
          entityName: 'post',
          channelIds: [],
          draftMode: true,
          queueingType: createQueueingType(option, true),
          isPlural: false,
          willCreateAnotherPost: options?.willCreateAnotherPost,
        })
        return saveResult(true)
      }

      const reportFailure = (
        errors: ReadonlyArray<{ message: string }>,
        message: string | null | undefined,
      ): ReturnType<typeof saveResult> => {
        setGeneralSaveErrors(
          errors.length > 0
            ? errors.map((e) => e.message)
            : message
              ? [message]
              : [GENERIC_SAVE_ERROR],
        )
        return saveResult(false)
      }

      try {
        if (draftId) {
          const { data } = await updateContentItemDraft({
            variables: {
              input: {
                id: draftId,
                draft,
                // Edit seeds selectedTagIds from the draft's tags, so this
                // reflects the current selection on the parent rather than
                // clearing it.
                tagIds: selectedTagIds,
                // Omit to preserve the existing date (an untouched schedule);
                // a picked time updates it. Clearing back to undated isn't wired.
                ...(targetDate ? { targetDate } : {}),
              },
            },
          })
          const payload = data?.updateContentItemDraft

          if (payload?.__typename === 'UpdateContentItemDraftSuccess') {
            // Create Another's remount is deferred a tick (ComposerDialog's
            // reopenAfterClose), so this store is still the one live briefly
            // after save. Clear the edit seed now or a save landing in that
            // window would update this same item again instead of creating
            // a new draft.
            if (options?.willCreateAnotherPost) {
              getComposerStore().getState().setOmniDraftId(null)
            }
            return reportSaved()
          }
          if (payload?.__typename === 'UpdateContentItemDraftFailure') {
            return reportFailure(payload.errors, payload.message)
          }
          setGeneralSaveErrors([GENERIC_SAVE_ERROR])
          return saveResult(false)
        }

        const { data } = await createContentItemDraft({
          variables: {
            input: {
              draft,
              organizationId,
              tagIds: selectedTagIds,
              ...(targetDate ? { targetDate } : {}),
              ...(title ? { title } : {}),
            },
          },
        })
        const payload = data?.createContentItemDraft

        if (payload?.__typename === 'CreateContentItemDraftSuccess') {
          return reportSaved()
        }
        if (payload?.__typename === 'CreateContentItemDraftFailure') {
          return reportFailure(payload.errors, payload.message)
        }

        setGeneralSaveErrors([GENERIC_SAVE_ERROR])
        return saveResult(false)
      } catch (error) {
        const messages = extractGraphQLMessages(error)
        setGeneralSaveErrors(
          messages.length > 0 ? messages : [GENERIC_SAVE_ERROR],
        )
        return saveResult(false)
      }
    },
    [
      organizationId,
      createContentItemDraft,
      updateContentItemDraft,
      option,
      scheduledAt,
      setGeneralSaveErrors,
      clearGeneralSaveErrors,
    ],
  )
}
